/** Funcionários dos clientes: cadastro do departamento pessoal (no formato do eSocial), ligado a tarefas, processos e documentos gerados. */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';
import { FuncionarioEsocial } from './esocial.js';
import { GradeHorario } from './jornada.js';

export const VinculoTrabalho = z.enum(['CLT', 'APRENDIZ', 'ESTAGIO', 'INTERMITENTE', 'TEMPORARIO', 'DOMESTICO', 'PRO_LABORE', 'AUTONOMO']);
export type VinculoTrabalho = z.infer<typeof VinculoTrabalho>;
export const ROTULO_VINCULO: Record<VinculoTrabalho, string> = {
  CLT: 'CLT', APRENDIZ: 'Aprendiz', ESTAGIO: 'Estágio', INTERMITENTE: 'Intermitente', TEMPORARIO: 'Temporário', DOMESTICO: 'Doméstico', PRO_LABORE: 'Sócio (pró-labore)', AUTONOMO: 'Autônomo',
};
export const SituacaoFuncionario = z.enum(['ATIVO', 'AFASTADO', 'DESLIGADO']);
export type SituacaoFuncionario = z.infer<typeof SituacaoFuncionario>;

const texto = (max: number) => z.string().trim().max(max).nullable().default(null);

export const FuncionarioBody = z.object({
  nome: z.string().trim().min(2).max(120),
  /** com ou sem pontuação; vazio = não informado */
  cpf: texto(14),
  matricula: texto(30), cargo: texto(80), cbo: texto(10), setor: texto(60),
  /** sem categoria do eSocial informada, vale este; com categoria, o vínculo sai dela */
  vinculo: VinculoTrabalho.default('CLT'),
  situacao: SituacaoFuncionario.default('ATIVO'),
  dataAdmissao: DataISO.nullable().default(null),
  dataDemissao: DataISO.nullable().default(null),
  /** término do contrato por prazo determinado (experiência, obra certa) */
  dataTermino: DataISO.nullable().default(null),
  salario: z.number().min(0).max(9_999_999).nullable().default(null),
  /** descrição da jornada por extenso (dscJorn do eSocial; é o que vai para o contrato). Vazia + grade = o sistema descreve */
  jornada: texto(999),
  /** vazia + grade = o sistema soma */
  horasSemanais: z.number().min(0).max(99).nullable().default(null),
  gradeHorario: GradeHorario.nullable().default(null),
  email: z.string().trim().max(200).email().nullable().default(null).or(z.literal('').transform(() => null)), telefone: texto(30),
  dataNascimento: DataISO.nullable().default(null),
  rg: texto(20), ctps: texto(30), pis: texto(14),
  /** texto livre para o contrato; vazio + país Brasil = "brasileiro(a)" pelo sexo */
  nacionalidade: texto(40),
  observacao: z.string().trim().max(2000).nullable().default(null),
  esocial: FuncionarioEsocial.default(() => FuncionarioEsocial.parse({})),
});
export type FuncionarioBody = z.infer<typeof FuncionarioBody>;

export const Funcionario = z.object({
  id: Id, clienteId: Id, nome: z.string(), cpf: z.string().nullable(), matricula: z.string().nullable(), cargo: z.string().nullable(), cbo: z.string().nullable(), setor: z.string().nullable(),
  vinculo: VinculoTrabalho, situacao: SituacaoFuncionario, dataAdmissao: DataISO.nullable(), dataDemissao: DataISO.nullable(), dataTermino: DataISO.nullable(), salario: z.number().nullable(),
  jornada: z.string().nullable(), horasSemanais: z.number().nullable(), gradeHorario: GradeHorario.nullable(),
  email: z.string().nullable(), telefone: z.string().nullable(), dataNascimento: DataISO.nullable(), rg: z.string().nullable(), ctps: z.string().nullable(), pis: z.string().nullable(),
  /** por extenso, no gênero da pessoa (sai do código do eSocial) */
  estadoCivil: z.string().nullable(), nacionalidade: z.string().nullable(),
  /** endereço numa linha só (montado dos campos do eSocial) */
  endereco: z.string().nullable(), observacao: z.string().nullable(),
  esocial: FuncionarioEsocial,
  /** o que falta para o S-2200 */
  pendenciasEsocial: z.array(z.string()),
  /** tarefas abertas ligadas a esta pessoa */
  tarefasAbertas: z.number().int(),
  /** férias ou afastamento em curso hoje */
  ocorrenciaAtual: z.object({ tipo: z.enum(['FERIAS', 'AFASTAMENTO']), motivoRotulo: z.string().nullable(), ate: DataISO.nullable() }).nullable().optional(),
  /** férias com o prazo de concessão acabando ou vencido */
  ferias: z.object({ situacao: z.enum(['VENCIDAS', 'VENCENDO']), limite: DataISO, saldo: z.number().int() }).nullable().optional(),
  criadoEm: z.string(),
});
export type Funcionario = z.infer<typeof Funcionario>;

// ---- importação por planilha (CSV já lido na tela; mesma mecânica da importação de clientes) ----
export const ImportarFuncionariosBody = z.object({ registros: z.array(z.record(z.string(), z.string())).min(1).max(2000), confirmar: z.boolean().default(false) });
export type ImportarFuncionariosBody = z.infer<typeof ImportarFuncionariosBody>;
export const ImportarFuncionariosResultado = z.object({
  total: z.number().int(), validos: z.number().int(), invalidos: z.number().int(), duplicados: z.number().int(), inseridos: z.number().int(),
  linhas: z.array(z.object({ linha: z.number().int(), nome: z.string(), cpf: z.string(), situacao: z.enum(['OK', 'ERRO', 'DUPLICADO']), erros: z.array(z.string()), avisos: z.array(z.string()) })),
});
export type ImportarFuncionariosResultado = z.infer<typeof ImportarFuncionariosResultado>;

/** Cabeçalhos aceitos (normalizados: minúsculo, sem acento/espaço). A folha exporta com nomes variados — daí os apelidos. */
export const COLUNAS_FUNCIONARIO: Record<string, string[]> = {
  nome: ['nome', 'nomecompleto', 'funcionario', 'empregado', 'colaborador', 'trabalhador'],
  cpf: ['cpf'],
  matricula: ['matricula', 'registro', 'codigo', 'cod', 'matriculaesocial'],
  cargo: ['cargo', 'funcao', 'cargofuncao'],
  cbo: ['cbo'],
  setor: ['setor', 'departamento', 'centrodecusto', 'lotacao'],
  dataAdmissao: ['admissao', 'dataadmissao', 'dtadmissao', 'dataadm'],
  dataDemissao: ['demissao', 'datademissao', 'desligamento', 'datadesligamento', 'rescisao', 'datarescisao'],
  dataNascimento: ['nascimento', 'datanascimento', 'dtnascimento'],
  salario: ['salario', 'salariobase', 'remuneracao', 'salariocontratual'],
  categoria: ['categoria', 'codcateg', 'categoriaesocial'],
  sexo: ['sexo', 'genero'],
  email: ['email'],
  telefone: ['telefone', 'celular', 'fone'],
  pis: ['pis', 'nis', 'pispasep', 'nit'],
  ctps: ['ctps', 'carteiradetrabalho'],
  rg: ['rg', 'identidade'],
  horasSemanais: ['horassemanais', 'cargahoraria', 'cargahorariasemanal', 'jornadasemanal'],
};
